import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

import { Button } from '@monolith/components/0-atoms/Button';
import Icon from '@monolith/components/0-atoms/Icon';
import { StaffImportWarningModal } from '../components/StaffImportWarningModal';
import { StaffImportCompletePanel } from '../components/StaffImportCompletePanel';
import { StaffImportConfirmModal } from '../components/StaffImportConfirmModal';
import { StaffImportForm } from '../components/StaffImportForm';
import { StaffImportGrid } from '../components/StaffImportGrid';
import { OnboardingShell } from '../components/OnboardingShell';
import { StaffImportStatusPanel } from '../components/StaffImportStatusPanel';
import { StaffImportToast } from '../components/StaffImportToast';
import { STAFF_IMPORT_PAGE_COPY } from '../constants/staffImport';
import { useBusinessQuery } from '../hooks/useBusinessQuery';
import { deriveProcessImportDisabledReason } from '../helpers/staffImportProcessImport';
import { useStaffImportFlow } from '../hooks/useStaffImportFlow';
import { staffImportTokens } from '../styles/staffImportTokens';

type StaffImportPageProps = {
    email: string;
};

const loadingStyle: React.CSSProperties = {
    fontFamily: 'Montserrat, sans-serif',
    padding: '32px 64px',
};

const errorStyle: React.CSSProperties = {
    ...loadingStyle,
};

const PageContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${staffImportTokens.spacing.sectionGap}px;
    padding: ${staffImportTokens.spacing.pagePaddingY}px
        ${staffImportTokens.spacing.pagePaddingX}px 48px;
    width: 100%;
    box-sizing: border-box;
`;

const ImportPreviewSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
`;

const FooterActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${staffImportTokens.spacing.sectionGap}px;
`;

const CancelButton = styled(Button)`
    color: ${staffImportTokens.colors.destructive};
`;

const ProcessImportButton = styled(Button)`
    gap: 8px;

    &:disabled {
        background-color: #8d8d8d;
        border-color: #8d8d8d;
        color: #ffffff;
        opacity: 1;
    }
`;

export const StaffImportPage = ({ email }: StaffImportPageProps) => {
    const { businessName } = useParams<{ businessName: string }>();
    const { data, loading, error } = useBusinessQuery(businessName);
    const {
        state,
        setSelectedFile,
        setGridRows,
        dismissToast,
        clearWebCrawlInlineError,
        uploadSelectedCsv,
        startWebCrawl,
        retryPolling,
        showConfirmModal,
        hideConfirmModal,
        processStaffImport,
        retryFailedRows,
        reset,
        cancelImport,
        hasExistingGridImport,
        showAbandonModal,
        pendingLeaveUrl,
        requestLeave,
        cancelLeave,
        confirmLeave,
        csvUploadedFileName,
        csvUploadLoading,
        csvDisabled,
        webCrawlLoading,
        webCrawlDisabled,
        statusProcessing,
    } = useStaffImportFlow(businessName || '');

    if (loading) {
        return <p style={loadingStyle}>Loading business...</p>;
    }

    if (error || !data?.business || !businessName) {
        return (
            <div style={errorStyle}>
                <h1>Staff Importer</h1>
                <p>
                    Business not found or you don&apos;t have access to{' '}
                    <strong>{businessName}</strong>.
                </p>
            </div>
        );
    }

    const business = data.business;
    const businessDisplayName =
        business.fullName || business.name || businessName;

    return (
        <OnboardingShell
            business={business}
            email={email}
            onBackLinkClick={(_event, url) => requestLeave(url)}
        >
            {state.toast ? (
                <StaffImportToast
                    toast={state.toast}
                    onDismiss={dismissToast}
                />
            ) : null}

            {state.step === 'complete' && state.processResult ? (
                <StaffImportCompletePanel
                    result={state.processResult}
                    onImportAnother={reset}
                    onRetryFailed={retryFailedRows}
                />
            ) : (
                <PageContent>
                    <StaffImportForm
                        businessDisplayName={businessDisplayName}
                        selectedFile={state.selectedFile}
                        uploadedFileName={csvUploadedFileName}
                        csvInlineError={state.csvInlineError}
                        webCrawlInlineError={state.webCrawlInlineError}
                        csvUploadLoading={csvUploadLoading}
                        csvDisabled={csvDisabled}
                        webCrawlLoading={webCrawlLoading}
                        webCrawlDisabled={webCrawlDisabled}
                        staffPageUrl={state.staffPageUrl}
                        hasExistingImport={hasExistingGridImport}
                        onFileSelect={setSelectedFile}
                        onUpload={uploadSelectedCsv}
                        onWebCrawl={startWebCrawl}
                        onWebCrawlUrlChange={clearWebCrawlInlineError}
                    />

                    <ImportPreviewSection aria-label="Import preview">
                        {state.uploadedFileName || statusProcessing ? (
                            <>
                                <StaffImportStatusPanel
                                    importSource={state.importSource}
                                    uploadedFileName={state.uploadedFileName}
                                    isImportJobProcessing={statusProcessing}
                                    analyzeResult={state.analyzeResult}
                                    completedAt={state.completedAt}
                                    isReadyForImport={state.isReadyForImport}
                                    showGridValidation={Boolean(
                                        state.analyzeResult,
                                    )}
                                />
                                {state.pollTimedOut ? (
                                    <Button
                                        id="staff-import-retry-polling"
                                        variant="accent-secondary"
                                        size="large"
                                        onClick={retryPolling}
                                    >
                                        Retry status check
                                    </Button>
                                ) : null}
                            </>
                        ) : null}
                        {state.analyzeResult ? (
                            <StaffImportGrid
                                rows={state.gridRows}
                                issues={state.analyzeResult.validation.issues}
                                existingBusinessEmails={
                                    state.analyzeResult
                                        .existingBusinessEmails ?? []
                                }
                                onRowsChange={setGridRows}
                            />
                        ) : null}
                    </ImportPreviewSection>

                    <FooterActions>
                        <CancelButton
                            id="staff-import-cancel"
                            variant="text-only"
                            size="large"
                            onClick={cancelImport}
                            disabled={state.processing}
                        >
                            {STAFF_IMPORT_PAGE_COPY.cancelLabel}
                        </CancelButton>
                        <ProcessImportButton
                            id="staff-import-process"
                            variant="primary"
                            size="large"
                            onClick={showConfirmModal}
                            disabled={
                                !state.isReadyForImport || state.processing
                            }
                            disabledReason={deriveProcessImportDisabledReason({
                                processing: state.processing,
                                statusProcessing,
                                analyzeResult: state.analyzeResult,
                                isReadyForImport: state.isReadyForImport,
                            })}
                        >
                            <Icon farIcon="arrow-right-to-bracket" />
                            {STAFF_IMPORT_PAGE_COPY.processImportLabel}
                        </ProcessImportButton>
                    </FooterActions>
                </PageContent>
            )}

            <StaffImportConfirmModal
                isOpen={state.showConfirmModal}
                processing={state.processing}
                staffCount={state.gridRows.length}
                onClose={hideConfirmModal}
                onConfirm={processStaffImport}
            />
            <StaffImportWarningModal
                isOpen={showAbandonModal}
                modalName="staff-import-abandon"
                title={
                    pendingLeaveUrl
                        ? STAFF_IMPORT_PAGE_COPY.abandonModalTitle
                        : STAFF_IMPORT_PAGE_COPY.abandonModalDiscardImportTitle
                }
                message={
                    pendingLeaveUrl
                        ? STAFF_IMPORT_PAGE_COPY.abandonModalMessage
                        : STAFF_IMPORT_PAGE_COPY.abandonModalDiscardImportMessage
                }
                dismissLabel={STAFF_IMPORT_PAGE_COPY.abandonModalCancelLabel}
                confirmLabel={
                    pendingLeaveUrl
                        ? STAFF_IMPORT_PAGE_COPY.abandonModalLeavePageLabel
                        : STAFF_IMPORT_PAGE_COPY.abandonModalDiscardImportLabel
                }
                dismissButtonId="staff-import-abandon-cancel"
                confirmButtonId="staff-import-abandon-leave-page"
                onDismiss={cancelLeave}
                onConfirm={confirmLeave}
            />
        </OnboardingShell>
    );
};

export default StaffImportPage;
